import { formatISO } from "date-fns";
import { z } from "zod";

import { Promotion } from "@/components/CurrPromotions";
import { SignInAction } from "@/data/models/SignInAction";
import { fetchJSONFactory } from "@/utils/fetch-json";

const fetchJSON = fetchJSONFactory(import.meta.env.VITE_USERS_API);

type SignInData = {
  email: string;
  password: string;
};

export type SignInResponse = {
  token: string;
  missingRequiredFields: boolean;
};

export type SignInWithCodeResponse = {
  token: string;
  missingRequiredFields: boolean;
  action?: SignInAction;
};

export async function signIn(data: SignInData) {
  try {
    return await fetchJSON<SignInResponse>("users/signin", {
      body: data,
      method: "POST",
    });
  } catch (error) {
    if (error instanceof Error && error.message === "INVALID_PASSWORD") {
      throw new Error(
        "The password you entered is incorrect. Please try again.",
      );
    }

    throw error;
  }
}

export async function signInWithCode(code: string) {
  try {
    return await fetchJSON<SignInWithCodeResponse>("users/signin_code", {
      body: { code },
      method: "POST",
    });
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === "INVALID_CODE") {
        throw new Error("INVALID_CODE");
      }
      if (error.message === "CODE_USED") {
        throw new Error("CODE_USED");
      }
    }

    throw error;
  }
}

type SignUpData = {
  firstName: string;
  lastName: string;
  email: string;
  dob: Date;
  password: string;
  marketingEmails: boolean;
  marketingTexts: boolean;
  phone: string;
};

export async function signUp(data: SignUpData) {
  try {
    return await fetchJSON<SignInResponse>("users/signup", {
      body: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        dob: formatISO(data.dob),
        password: data.password,
        origin: "consumer-web",
        allow_marketing_email: data.marketingEmails,
        allow_marketing_text: data.marketingTexts,
        phone_number: data.phone,
      },
      method: "POST",
    });
  } catch (error) {
    if (!(error instanceof Error)) {
      throw error;
    }
    switch (error.message) {
      case "USER_EXISTS": {
        throw new Error("User already exists");
      }
      case "INVALID_PHONE": {
        throw new Error("Invalid phone number");
      }
      default: {
        throw error;
      }
    }
  }
}

export type RequiredFieldsData = {
  first_name: string;
  last_name: string;
  dob: string;
  allow_marketing_email: boolean;
};

export async function updateRequiredFields(data: RequiredFieldsData) {
  const { first_name, last_name, dob, allow_marketing_email } = data;

  try {
    return await fetchJSON<SignInResponse>("users/info", {
      body: {
        first_name: first_name,
        last_name: last_name,
        dob: formatISO(dob),
        origin: "consumer-web",
        allow_marketing_email: allow_marketing_email,
      },
      method: "POST",
    });
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === "INVALID_DOB") {
        throw new Error(
          "Sorry, we currently don't support online accounts for customers under 13. Please see or call your local Restore staff for assistance with your account creation in person",
        );
      }
    }

    throw error;
  }
}

export type EmailStatusResponse = {
  customer: {
    guid: string;
    first_name: string;
    last_name: string;
  };
  has_password: boolean;
  has_multiple_customers: boolean;
};

export function emailStatus(email: string) {
  return fetchJSON<EmailStatusResponse>(`users/email_status`, {
    params: { email },
  });
}

export type RequestPasswordResetCodeResponse = {
  success: true;
};

export async function requestPasswordResetCode(email: string) {
  try {
    const response = await emailStatus(email);

    return await fetchJSON<RequestPasswordResetCodeResponse>(
      `users/request_password_reset_code`,
      {
        body: {
          customer_guid: response.customer.guid,
        },
        method: "POST",
      },
    );
  } catch (error) {
    if (error instanceof Error && error.message === "INVALID_EMAIL") {
      throw new Error(
        "Sorry, we don’t see this email in our records. Please try a different email or sign up for a new account",
      );
    }

    throw error;
  }
}

type ChangePasswordData = {
  code: string;
  password: string;
};

export async function changePassword(data: ChangePasswordData) {
  try {
    return await fetchJSON<SignInResponse>(`users/change_password_from_code`, {
      body: data,
      method: "POST",
    });
  } catch (error) {
    if (error instanceof Error && error.message === "CODE_USED") {
      throw new Error("Code has already been used");
    }

    throw error;
  }
}

type RequestSignInCodeResponse = {
  success: true;
};

export async function requestSignInCode(email: string) {
  const response = await emailStatus(email);

  return await fetchJSON<RequestSignInCodeResponse>(
    `users/request_signin_code`,
    {
      body: { customer_guid: response.customer.guid },
      method: "POST",
    },
  );
}

type ExchangeStaffClientOneTimeCodeResponse = {
  jwt: string;
  redirect_url: string | null;
  missingRequiredFields: boolean;
};

type ExchangeStaffClientOneTimeCodeParams = {
  code: string;
};

export function exchangeStaffClientOneTimeCode(
  params: ExchangeStaffClientOneTimeCodeParams,
) {
  return fetchJSON<ExchangeStaffClientOneTimeCodeResponse>(
    "auth/staff-client/token",
    {
      params: {
        code: params.code,
      },
      method: "POST",
    },
  );
}

export type UserInfo = {
  email: string;
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  first_name: string;
  last_name: string;
  mobile_phone: string | null;
  gender: string | null;
  emergency_name: string | null;
  emergency_phone: string | null;
  country: string | null;
  guid: string;
  core_waiver_expires: string | null;
  has_valid_core_waiver: boolean;
  has_card_on_file: boolean;
  dob: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
};

type UpdateUserData = Partial<UserInfo>;

export type UserInfoResponse = UserInfo;

export async function updateUser(data: UpdateUserData) {
  try {
    return await fetchJSON<UserInfoResponse>(`users/info`, {
      body: data,
      method: "PATCH",
    });
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === "INVALID_EMERGENCY_PHONE") {
        throw new Error("Invalid emergency phone number");
      }
      if (error.message === "INVALID_DOB") {
        throw new Error(
          "Sorry, we currently don't support online accounts for customers under 13. Please see or call your local Restore staff for assistance with your account creation in person",
        );
      }
      if (error.message === "INVALID_MOBILE_PHONE") {
        throw new Error("Invalid phone number");
      }
      if (error.message === "INVALID_GUARDIAN_PHONE") {
        throw new Error("Invalid guardian phone number");
      }
    }

    throw error;
  }
}

export async function fetchUser() {
  return await fetchJSON<UserInfoResponse>(`users/info`);
}

type ChangeEmailData = {
  email_old: string;
  email_new: string;
  password: string;
};

export async function changeEmail(data: ChangeEmailData) {
  try {
    return await fetchJSON<UserInfoResponse>(`users/change_email`, {
      body: data,
      method: "POST",
    });
  } catch (error) {
    if (error instanceof Error) {
      if (error.message === "INVALID_PASSWORD") {
        throw new Error(
          "The password you entered is incorrect. Please try again.",
        );
      } else if (error.message === "USER_EXISTS") {
        throw new Error("That email is already in use. Please try again.");
      }
    }

    throw error;
  }
}

type UserDeletionRequestResponse = {
  date_requested: string;
  customer_guid: string;
  has_account_balance: boolean;
  has_active_membership: boolean;
  can_submit_delete_request: boolean;
};

export function fetchDeletionRequest() {
  return fetchJSON<UserDeletionRequestResponse>(`users/deletion_request`);
}

export function createDeletionRequest(password: string) {
  return fetchJSON<UserDeletionRequestResponse>(`users/deletion_request`, {
    body: { password },
    method: "POST",
  }).catch((error) => {
    if (error.message === "INVALID_PASSWORD") {
      throw new Error(
        "The password you entered is incorrect. Please try again.",
      );
    } else if (error.message === "DELETION_REQUEST_ALREADY_EXISTS") {
      throw new Error("You have already requested to delete your account");
    }

    throw error;
  });
}

export function saveCampaignQuery(query: string) {
  return fetchJSON<{ success: true }>(`users/campaigns/saw`, {
    body: { query },
    method: "POST",
  });
}

export function identifyAnonUser(guid: string) {
  return fetchJSON<{ success: true }>(`users/identify-anon-user`, {
    body: { guid },
    method: "POST",
  });
}

export function forgotEmail(phone: string) {
  return fetchJSON<{ success: true }>("auth/forgot-email", {
    body: { phone: phone },
    method: "POST",
  });
}

export function phoneStatus(phone: string) {
  return fetchJSON<{ known: boolean }>("users/phone-status", {
    params: { phone: phone },
    method: "GET",
  });
}

export type CommunicationSettingsChannel = "email" | "text";
export type ChannelAttribute = "promo" | "schedule";

const CommunicationSettingsChannelItem = z.object({
  allOff: z.boolean(),
  promo: z.boolean(),
  schedule: z.boolean(),
  scheduleDescription: z.string(),
  promoDescription: z.string(),
});

export type CommunicationSettingsChannelItem = z.infer<
  typeof CommunicationSettingsChannelItem
>;

const CommunicationSettingsResponse = z.object({
  push_subscribe: z.enum(["subscribed", "unsubscribed", "opted_in"]),
  push_attributes: z.array(
    z.object({
      key: z.string(),
      value: z.boolean(),
      description: z.string(),
    }),
  ),
  email: CommunicationSettingsChannelItem,
  text: CommunicationSettingsChannelItem,
});

type CommunicationSettingsResponse = z.infer<
  typeof CommunicationSettingsResponse
>;

export function communicationSettings() {
  return fetchJSON<CommunicationSettingsResponse>(
    "users/communication-settings",
    {
      method: "GET",
    },
  );
}

export type UpdateCommunicationSettingsPayload = {
  email?: {
    all?: boolean;
    promo?: boolean;
    schedule?: boolean;
  };
  text?: {
    all?: boolean;
    promo?: boolean;
    schedule?: boolean;
  };
};

export function updateCommunicationSettings(params: {
  payload: UpdateCommunicationSettingsPayload;
}) {
  return fetchJSON<CommunicationSettingsResponse>(
    "users/communication-settings",
    {
      body: params.payload,
      method: "PATCH",
    },
  );
}

export type FetchUserPromosResponse = Promotion[];

export async function fetchUserPromos() {
  return fetchJSON<FetchUserPromosResponse>("users/promo-codes", {
    method: "GET",
  });
}
